import { CaretRightIcon } from '@radix-ui/react-icons';
import { Badge, Flex, Heading, IconButton, Text } from '@radix-ui/themes';
import { FilledCircleIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableHoC } from 'components/common/table';
import { TrainingPerfPlots } from 'components/sections/admin-portal/machines/dialogs/training-performance/plots';
import { StackedText } from 'components/sections/admin-portal/machines/dialogs/training-performance/stacked-text';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IItrLogExt } from 'lib_ts/interfaces/training/i-training-log';
import { useEffect, useMemo, useState } from 'react';

export const TrainingPerfIterationsTable = (props: {
  iterations: IItrLogExt[]; // b/c of mixin rgb
}) => {
  const loading = useTrainingPerformanceStore((s) => s.loading);
  const selectedLog = useTrainingPerformanceStore((s) => s.selectedLog);

  const columns = useMemo(() => {
    const output: ITypedTableColumn<IItrLogExt>[] = [
      {
        label: 'Iteration',
        key: 'index',
        align: 'center',
        formatFn: (m) => (
          <Flex gap="2">
            <Text style={{ color: `rgb(${m.rgb})` }}>
              <FilledCircleIcon />
            </Text>
            <Text>{m.index + 1}</Text>
          </Flex>
        ),
      },
      {
        label: 'Shots',
        key: 'shots',
        align: 'center',
        formatFn: (m) => (
          <Flex gap="1" align="center" justify="center">
            <Text>{m.shotIDs.length}</Text>
            <IconButton
              size="1"
              className="btn-floating"
              disabled={m.shotIDs.length === 0}
              variant={RADIX.BUTTON.VARIANT.BORDERLESS}
              color={RADIX.COLOR.NEUTRAL}
              onClick={() =>
                useTrainingPerformanceStore.getState().setSelectedIteration(m)
              }
            >
              <CaretRightIcon />
            </IconButton>
          </Flex>
        ),
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.shotIDs.length);
          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Smart Sampling',
        key: 'smart-sampling',
        align: 'center',
        formatFn: (m) =>
          m.preset.spec.smartSampling ? (
            <Badge color={RADIX.COLOR.SUCCESS}>Yes</Badge>
          ) : (
            <Badge color={RADIX.COLOR.NEUTRAL}>No</Badge>
          ),
        sortRowsFn: (a, b, dir) => {
          if (a.preset.spec.smartSampling === b.preset.spec.smartSampling) {
            return -dir;
          }

          if (a.preset.spec.smartSampling) {
            return dir;
          }

          return -dir;
        },
      },
      {
        label: 'PT Rel. Thresh.',
        key: 'pt-rel-thresh',
        align: 'center',
        formatFn: (m) =>
          selectedLog
            ? TrainingHelper.getIterationPtRelThreshold({
                priority: selectedLog.pitchPriority,
                ball_type: selectedLog.ball_type,
                itr: m,
              }).toFixed(2)
            : undefined,
        sortRowsFn: (a, b, dir) => {
          if (!selectedLog) {
            return -dir;
          }

          const [aVal, bVal] = [a, b].map((m) =>
            TrainingHelper.getIterationPtRelThreshold({
              priority: selectedLog.pitchPriority,
              ball_type: selectedLog.ball_type,
              itr: m,
            })
          );

          return aVal <= bVal ? -dir : dir;
        },
      },
      {
        label: 'Metric',
        key: 'metric',
        disableSort: true,
        formatFn: () => <StackedText line1="Error" line2="St. Dev" />,
      },
      {
        label: 'Speed',
        subLabel: 'mph',
        key: 'speed',
        align: 'center',
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.vMph);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.vMph.toFixed(1)}
            line2={m.stDev.vMph.toFixed(1)}
          />
        ),
      },
      {
        label: 'Break X',
        subLabel: 'in',
        key: 'breakXIn',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Breaks,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.breakXIn ?? 0);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.breakXIn?.toFixed(1)}
            line2={m.stDev.breakXIn?.toFixed(1)}
          />
        ),
      },
      {
        label: 'Break Z',
        subLabel: 'in',
        key: 'breakZIn',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Breaks,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.breakZIn ?? 0);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.breakZIn?.toFixed(1)}
            line2={m.stDev.breakZIn?.toFixed(1)}
          />
        ),
      },
      {
        label: 'Spin X',
        subLabel: 'rpm',
        key: 'wxRpm',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Spins,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.wxRpm ?? 0);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.wxRpm?.toFixed(0)}
            line2={m.stDev.wxRpm?.toFixed(0)}
          />
        ),
      },
      {
        label: 'Spin Y',
        subLabel: 'rpm',
        key: 'wyRpm',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Spins,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.wyRpm ?? 0);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.wyRpm?.toFixed(0)}
            line2={m.stDev.wyRpm?.toFixed(0)}
          />
        ),
      },
      {
        label: 'Spin Z',
        subLabel: 'rpm',
        key: 'wzRpm',
        align: 'center',
        invisible: selectedLog?.pitchPriority !== BuildPriority.Spins,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.wzRpm ?? 0);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.wzRpm?.toFixed(0)}
            line2={m.stDev.wzRpm?.toFixed(0)}
          />
        ),
      },
      {
        label: 'Plate X',
        subLabel: 'in',
        key: 'plateXIn',
        align: 'center',
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.plateXIn);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.plateXIn.toFixed(1)}
            line2={m.stDev.plateXIn.toFixed(1)}
          />
        ),
      },
      {
        label: 'Plate Z',
        subLabel: 'in',
        key: 'plateZIn',
        align: 'center',
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => m.meanError.plateZIn);
          return aVal <= bVal ? -dir : dir;
        },
        formatFn: (m) => (
          <StackedText
            line1={m.meanError.plateZIn.toFixed(1)}
            line2={m.stDev.plateZIn.toFixed(1)}
          />
        ),
      },
    ];

    return output;
  }, [selectedLog]);

  const [checked, setChecked] = useState<string[]>([]);

  // reset whenever iterations changes
  useEffect(() => {
    setChecked([]);
  }, [props.iterations]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminTrainingPerfShots,
    total: props.iterations.length,
    enablePagination: true,
    pageSizes: TABLES.PAGE_SIZES.SM,
  };

  const checkable = useMemo(
    (): ITableCheckable => ({
      checkboxColumnIndex: 0,
      afterCheckOne: (id, checked) => {
        if (checked) {
          setChecked((prev) => ArrayHelper.unique([...prev, id]));
          return;
        }

        setChecked((prev) => prev.filter((m) => m !== id));
      },
      afterCheckAll: (mode) => {
        switch (mode) {
          case 'all': {
            setChecked(props.iterations.map((m) => m._id));
            break;
          }
          case 'none': {
            setChecked([]);
            break;
          }
          case 'some':
          default: {
            // shouldn't trigger
            break;
          }
        }
      },
      checkedActions: undefined,
    }),
    [props.iterations]
  );

  const plotIterations = useMemo(() => {
    if (checked.length === 0) {
      return props.iterations;
    }

    return props.iterations.filter((m) => checked.includes(m._id));
  }, [props.iterations, checked]);

  return (
    <ErrorBoundary identifier="TrainingPerfIterationsTable">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <TrainingPerfPlots iterations={plotIterations} />

        <Heading size="3">Iterations</Heading>

        <CommonTableHoC
          identifier="TrainingPerfIterationsTable"
          dataTestID="TrainingPerfIterationsTable"
          displayColumns={columns}
          displayData={props.iterations}
          loading={loading}
          {...pagination}
          {...checkable}
          enableSort
        />
      </Flex>
    </ErrorBoundary>
  );
};
